import { TcoService } from '@amzn/awscat-portfolio-assessment-service-client';
import { AwsEC2InstanceType, OutputEc2 } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Box, Container, SpaceBetween, Tabs, TabsProps } from '@amzn/awsui-components-react';
import { useContext, useEffect, useState } from 'react';
import { match, useRouteMatch } from 'react-router';
import { useHistory } from 'react-router-dom';
import { useIsMounted } from 'react-tidy';

import { HelpPanelTopicOptions } from '../../../../common/Constants';
import { constants } from '../../../../common/Localization';
import { formatPreInstalledSw } from '../../../../common/LocalizationHelper';
import AWSCATTable from '../../../../common/table/Table-index';
import { ColumnDefinition, ColumnTypes } from '../../../../common/table/types';
import { MatchParam } from '../../../../common/types';
import { HelpPanelContext } from '../../../../contexts/HelpPanelContext';
import RumContext from '../../../../contexts/RumContext';
import { TcoContext } from '../../../../contexts/TcoContext';
import { InfoLink } from '../../../common/InfoLink';
import PreviewBanner from '../../../common/PreviewBanner';
import { ErrorMsgFormat } from '../../error-handler-context/CommonTcoErrorHandler';
import { generateEc2ErrorMsgs } from '../../error-handler-context/helper';
import GenerateErrorMessageBox from '../../error-handler-context/TcoErrorHandler';
import DedicatedHosts from './DedicatedHosts';
import EC2Instances from './EC2Instances';
import VMware from './VMware';

type Props = FlashContextInterface;

const tcoService = new TcoService();

function EC2Recommendation({ flash }: Props): JSX.Element | null {
    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId/tco/:tcoAssessmentId',
    });
    const tcoAssessmentId = match?.params?.tcoAssessmentId ?? null;
    const portfolioId = match?.params.portfolioId ?? null;

    const { assessment } = useContext(TcoContext);
    const { panelHandler } = useContext(HelpPanelContext);
    const { recordEvent } = useContext(RumContext);

    const [ loading, setLoading ] = useState(false);
    const [ ec2Output, setEc2Output ] = useState<OutputEc2>();
    const [ errorMessages, setErrorMessages ] = useState<ErrorMsgFormat[]>([]);
    const history = useHistory();
    const isMounted = useIsMounted();

    const defaultDetailsTabs: TabsProps.Tab[] = [
        {
            label: constants.EC2_INSTANCES,
            id: 'ec2',
        },
        {
            label: constants.DEDICATED_HOSTS,
            id: 'host',
        },
    ];

    const [ detailsTabs, setDetailsTabs ] = useState<TabsProps.Tab[]>(defaultDetailsTabs);

    useEffect(() => {
        panelHandler(HelpPanelTopicOptions.EC2_RECOMMENDATIONS);
    }, [ panelHandler ]);

    useEffect(() => {
        if (assessment) {
            if (assessment.configuration.tcoAssumptions.relocateOn && detailsTabs.length === 2) {
                setDetailsTabs([
                    ...defaultDetailsTabs,
                    {
                        label: constants.VMWARE_CLOUD,
                        id: 'VMware',
                    },
                ]);
            } else if (!assessment.configuration.tcoAssumptions.relocateOn && detailsTabs.length === 3) {
                setDetailsTabs([...defaultDetailsTabs]);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ assessment ]);
        
    const [ activeDetailTab, setActiveDetailTab ] = useState(detailsTabs[0].id);

    const loadEc2Assessment = async () => {
        if (!isMounted()) {
            return;
        }
        if (tcoAssessmentId && tcoAssessmentId !== 'default' && assessment) { 
            try {
                setLoading(true);
                const { output } = await tcoService.getAwsEC2(tcoAssessmentId);
                if (isMounted()) {
                    setEc2Output(output.aws.ec2);
                }
            } catch (err) {
                if (isMounted()) {
                    flash.alert(FlashType.error, (err as Error).message, constants.ERROR_LOADING_EC2_ASSESSMENT);
                }
            } finally {
                if (isMounted()) {
                    setLoading(false);
                }
            }
        }
    };

    useEffect(() => {
        if (assessment) {
            loadEc2Assessment();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ assessment ]);

    useEffect(() => {
        if (!ec2Output || !portfolioId) {
            return;
        }
        setErrorMessages(generateEc2ErrorMsgs(ec2Output, portfolioId));
    }, [ ec2Output, portfolioId ]);

    const awsServerRecommendationTableColumnDefinition: ColumnDefinition<AwsEC2InstanceType>[] = [
        { header: constants.INSTANCE_TYPE, id: 'instanceType', type: ColumnTypes.TEXT, cell: item => item.instanceType, text: constants.INSTANCE_TYPE },
        { 
            header: constants.V_CPU, 
            id: 'vcpu', 
            type: ColumnTypes.NUMBER, 
            data: item => (item.vcpuOnUI ?? item.vcpu) || '-',
            cell: item => <Box float='right'>{(item.vcpuOnUI ?? item.vcpu) || '-'}</Box>, 
            text: constants.V_CPU 
        },
        {   header: constants.MEMORY,
            id: 'memory',
            type: ColumnTypes.NUMBER,
            data: item => item.memory || '-',
            cell: item => <Box float='right'>{item.memory}</Box>,
            text: constants.MEMORY,
        },
        {
            header: constants.OPERATING_SYSTEM,
            id: 'operatingSystem',
            type: ColumnTypes.TEXT,
            data: item => item.operatingSystem || '-',
            cell: item => item.operatingSystem || '-',
            text: constants.OPERATING_SYSTEM,
        },
        {
            header: constants.PREINSTALLED_SOFTWARE,
            id: 'preInstalledSw',
            type: ColumnTypes.TEXT,
            data: item => formatPreInstalledSw(item?.preInstalledSw),
            cell: item => formatPreInstalledSw(item?.preInstalledSw),
            text: constants.PREINSTALLED_SOFTWARE,
        },
        { 
            header: constants.TENANCY,
            id: 'tenancy',
            type: ColumnTypes.TEXT,
            cell: item => item.tenancy,
            text: constants.TENANCY
        },
        {
            header: constants.RECOMMENDED_PAYMENT_OPTION,
            id: 'lowestOfferName',
            type: ColumnTypes.TEXT,
            data: item => ec2Output?.pricing.offerDetails[item.lowestOfferName] ?? item.lowestOfferName,
            cell: item => ec2Output?.pricing.offerDetails[item.lowestOfferName] ?? item.lowestOfferName,
        },
        {
            header: constants.COUNT,
            id: 'numInstances',
            type: ColumnTypes.NUMBER,
            data: item => item.numInstances || '-',
            cell: item => <Box float='right'>{item.numInstances || '-'}</Box>,
            text: constants.COUNT,
        },
    ];

    const tableInfo = (
        <InfoLink topic={HelpPanelTopicOptions.EC2_RECOMMENDATIONS} />
    );

    const AwsServerRecommendationsTable = ec2Output ? 
        (
            <AWSCATTable
                resourceName={constants.AWS_SERVER_RECOMMENDATIONS}
                columnDefinitions={awsServerRecommendationTableColumnDefinition}
                items={ec2Output.instanceTypes}
                loading={loading}
                headerInfo={tableInfo}
                resizeable
                autoGenerateVisibleContent
                useDefaultDownload
                enableAutoSorting
            />
        ) : null;

    const detailsSection = ec2Output ? (
        <div data-testid='ec2Details'>
            <h2>{constants.DETAILS}</h2>
            <Tabs
                tabs={detailsTabs}
                activeTabId={activeDetailTab}
                onChange={event => {
                    // record a RUM event when the user changes tabs
                    recordEvent({type: `EC2RecommendationTabChange-${event.detail.activeTabId}`, data: {user_interaction: 'click'}});
                    setActiveDetailTab(event.detail.activeTabId);
                }}
            />
            {activeDetailTab === 'ec2' ? <EC2Instances instanceTypeResults={ec2Output?.instanceTypeResults ?? []} /> : null}
            {activeDetailTab === 'host' ? (
                <DedicatedHosts
                    dedicatedHostRecommendations={ec2Output?.dedicatedHostRecommendations ?? []}
                    dedicatedHostServers={ec2Output?.dedicatedHostServers ?? []}
                />
            )
                : null
            }
            {activeDetailTab === 'VMware' ? <VMware vmwareSizeAssessmentServers={ec2Output?.vmwareSizeAssessment.servers ?? []} />
                : null
            }

        </div>
    ) : null;

    const DHPreviewAlert = (
        <PreviewBanner
            customHeader={constants.DEDICATED_HOST_IN_PREVIEW_HEADER}
            customText={constants.DEDICATED_HOST_IN_PREVIEW}
        />
    );

    const content = (
        <SpaceBetween size='l'>
            {(ec2Output?.dedicatedHostRecommendations || []).length > 0 || (ec2Output?.failedDedicatedHostServers || []).length > 0 ? DHPreviewAlert : null}
            <GenerateErrorMessageBox
                errorMessages={errorMessages}
                history={history}
            />
            {AwsServerRecommendationsTable}
            {detailsSection}
        </SpaceBetween>
    );
    return (
        <Container data-testid='ec2Recommendation'>
            {content}
        </Container>
    );
}

export default withFlashContext(EC2Recommendation);
